// BBS Guestbook page
// Posts randomly mutate to different book variants over time.
// Hidden ARG terminal accessible via LOGIN command.

import {
  getInitialGuestbookMessages,
  getAllGuestbookMessages,
  setupGuestbookMutation
} from '../scripts/content-randomizer';
import { addCRTFlicker, addScanlines } from '../scripts/effects';
import { createReturnToHouse } from '../scripts/navigation';
import { showARGTerminal } from '../components/ARGTerminal';

// Track typed characters for LOGIN detection
let typedBuffer = '';
const LOGIN_COMMAND = 'login';

interface GuestbookMessage {
  id: string;
  timestamp: string;
  author: string;
  content: string;
  corrupted?: boolean;
}

// Track cleanup functions
let cleanupFunctions: (() => void)[] = [];

/**
 * Handle keydown events for LOGIN command detection
 */
function handleKeydown(e: KeyboardEvent): void {
  // Ignore if user is typing in an input field
  if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
    return;
  }

  // Only track letters
  if (e.key.length === 1 && /[a-zA-Z]/.test(e.key)) {
    typedBuffer += e.key.toLowerCase();

    // Keep buffer at reasonable length
    if (typedBuffer.length > 20) {
      typedBuffer = typedBuffer.slice(-20);
    }

    // Check if LOGIN command was typed
    if (typedBuffer.endsWith(LOGIN_COMMAND)) {
      typedBuffer = '';
      showARGTerminal();
    }
  }

  // Clear buffer on escape or special keys
  if (e.key === 'Escape' || e.key === 'Enter') {
    typedBuffer = '';
  }
}

/**
 * Render the guestbook page
 */
export function renderGuestbook(): void {
  const app = document.getElementById('app');
  if (!app) return;

  app.innerHTML = '';
  cleanupFunctions = [];

  const container = document.createElement('div');
  container.className = 'guestbook-container guestbook-scanlines';

  // Get randomized initial messages from the pool
  const messages = getInitialGuestbookMessages(12);
  const allMessages = getAllGuestbookMessages();

  // Track pool indices for each displayed message
  const poolIndices = messages.map(msg =>
    allMessages.findIndex(m => m.id === msg.id)
  );

  const messagesList = messages
    .map(
      (msg, index) => `
      <article class="guestbook-message${msg.corrupted ? ' guestbook-message--corrupted' : ''}" data-index="${index}" data-pool-index="${poolIndices[index]}" data-corrupted="${msg.corrupted || false}">
        <div class="guestbook-message-header">
          <span class="guestbook-message-index">[${String(index + 1).padStart(3, '0')}]</span>
          <span class="guestbook-message-timestamp">${escapeHtml(msg.timestamp)}</span>
          <span class="guestbook-message-author">- ${escapeHtml(msg.author)}</span>
        </div>
        <div class="guestbook-message-content">${escapeHtml(msg.content)}</div>
      </article>
    `
    )
    .join('');

  container.innerHTML = `
    <!--
      ┌────────────────────────────────────────────────────────────────────┐
      │  BBS NODE 7 - SYSTEM MESSAGE                                      │
      │                                                                    │
      │  This is the public guestbook. But there is also the ARCHIVE.     │
      │                                                                    │
      │  The archive contains sealed threads that predate the novel's     │
      │  publication. Posts from users who could not have existed.        │
      │  Timestamps that do not correspond to linear time.                │
      │                                                                    │
      │  To access the sealed archive:                                    │
      │    > Return to the house and type "vectors" or "manuscript"       │
      │    > Or navigate directly to #archive                             │
      │    > Or: __chopras.discover('archive')                            │
      │                                                                    │
      │  There are also the BASEMENT and ATTIC...                         │
      │  But SysOp does not recommend visiting those nodes.               │
      │                                                                    │
      │  CARRIER STABLE - PROCEED WITH CAUTION                            │
      └────────────────────────────────────────────────────────────────────┘
    -->
    <div class="guestbook-terminal guestbook-crt">
      <header class="guestbook-header" id="main-content" tabindex="-1">
        CHOPRAS BBS v1.0 - NODE 7 - 2400 BAUD
      </header>
      <div class="guestbook-divider">═══════════════════════════════════════════════════════</div>

      <div class="guestbook-command">> LIST MESSAGES</div>

      <div class="guestbook-messages" role="list" aria-label="Guestbook messages">
        ${messagesList}
      </div>

      <div class="guestbook-actions">
        <div class="guestbook-action">
          <button type="button" class="guestbook-terminal-btn" aria-label="Open terminal to leave a message">> LEAVE MESSAGE</button>
        </div>
        <div class="guestbook-action guestbook-action--nav"></div>
      </div>
    </div>
  `;

  app.appendChild(container);

  // Add CRT effects
  const terminal = container.querySelector('.guestbook-terminal');
  if (terminal) {
    addCRTFlicker(terminal as HTMLElement);
    addScanlines(terminal as HTMLElement);
  }

  // Add return to house navigation (green terminal style)
  const navContainer = container.querySelector('.guestbook-action--nav');
  if (navContainer) {
    const returnBtn = createReturnToHouse({ className: 'return-to-house--green' });
    navContainer.appendChild(returnBtn);
  }

  // Setup terminal button click handler
  const terminalBtn = container.querySelector('.guestbook-terminal-btn');
  if (terminalBtn) {
    terminalBtn.addEventListener('click', () => {
      showARGTerminal();
    });
  }

  // Setup live mutations
  setupLiveMutations(container);

  // Setup LOGIN command detection
  typedBuffer = '';
  document.addEventListener('keydown', handleKeydown);
}

/**
 * Escape HTML special characters to prevent XSS
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Setup live content mutations for guestbook messages
 * Messages will randomly change to different book variants over time
 */
function setupLiveMutations(container: HTMLElement): void {
  const items = container.querySelectorAll('.guestbook-message');

  items.forEach((item) => {
    const el = item as HTMLElement;
    const poolIndex = parseInt(el.dataset.poolIndex || '0', 10);

    const cleanup = setupGuestbookMutation(el, poolIndex, (newMessage: GuestbookMessage, newIndex: number) => {
      // Update the content
      const timestampEl = el.querySelector('.guestbook-message-timestamp');
      const authorEl = el.querySelector('.guestbook-message-author');
      const contentEl = el.querySelector('.guestbook-message-content');

      if (timestampEl) {
        timestampEl.textContent = newMessage.timestamp;
      }

      if (authorEl) {
        authorEl.textContent = `- ${newMessage.author}`;
      }

      if (contentEl) {
        contentEl.textContent = newMessage.content;
      }

      // Update corrupted state
      if (newMessage.corrupted) {
        el.classList.add('guestbook-message--corrupted');
      } else {
        el.classList.remove('guestbook-message--corrupted');
      }

      // Update pool index for next mutation
      el.dataset.poolIndex = newIndex.toString();
      el.dataset.corrupted = String(newMessage.corrupted || false);
    });

    cleanupFunctions.push(cleanup);
  });
}

/**
 * Cleanup guestbook page
 */
export function cleanupGuestbook(): void {
  cleanupFunctions.forEach(fn => fn());
  cleanupFunctions = [];

  // Remove LOGIN command listener
  document.removeEventListener('keydown', handleKeydown);
  typedBuffer = '';
}
